import React, {
  useCallback, useContext, useEffect, useMemo, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ButtonBar, ErrorMessage,
  RadioCardOptionGroup,
  ScreenHeader
} from '../../components';
import { useCreateOrUpdate, useFormData } from '../../hooks';

import { NewFAContext } from '.';

const formConfig = {
  fields: [
    { 
      id: 'disposition', 
      fieldName: 'disposition',
      type: 'text' as const,
      required: true 
    }
  ]
};

const options = [
  {
    id: 'burial',
    label: 'Burial',
    description: 'Plan for burial costs and arrangements.',
    name: 'disposition',
    icon: 'deceased',
    value: 'burial'
  },
  {
    id: 'cremation',
    label: 'Cremation',
    description: 'Plan for cremation costs and arrangements.',
    name: 'disposition',
    icon: 'mode_heat',
    value: 'cremation'
  },
  {
    id: 'donation',
    label: 'Donation',
    description: 'Plan on donating my body to science or an organization.',
    name: 'disposition',
    icon: 'person_heart',
    value: 'donation'
  },
  {
    id: 'no-preference',
    label: 'No Preference',
    description: 'Let my family decide on the best option.',
    name: 'disposition',
    icon: 'help_outline',
    value: 'no-preference'
  }
];

export function NewFADisposition() {
  const navigate = useNavigate();
  const { funeralArrangement, update } = useContext(NewFAContext);
  const { handleCreateOrUpdate } = useCreateOrUpdate('funeral_arrangements');
  const { errors } = useFormData(formConfig);

  const [ error, setError ] = useState<string | null>(null);

  // Seed existing data into form fields
  useEffect(() => {
    console.log('NewFADisposition useEffect', funeralArrangement);
    if (funeralArrangement?.disposition) {
      const radioElement = document.querySelector(`input[name="disposition"][value="${funeralArrangement.disposition}"]`) as HTMLInputElement;
      if (radioElement) {
        radioElement.checked = true;
      }
    }
  }, [ funeralArrangement ]);
    
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [ navigate ]);

  const handleNext = useCallback(async () => {
    const selectedElement = document.querySelector('input[name="disposition"]:checked');

    if (!selectedElement) {
      setError('Please select a preference.');
      return;
    }

    const selectedOption = (selectedElement as HTMLInputElement).value;
    
    // Save the disposition preference
    const payload = {
      disposition: selectedOption,
      user_id: funeralArrangement?.user_id || null,
      person_id: funeralArrangement?.person_id || null
    };

    try {
      await handleCreateOrUpdate(funeralArrangement?.id || '', payload);
      await update();
    } catch (err) {
      console.error('Error saving disposition preference:', err);
      setError('Failed to save preference. Please try again.');
      return;
    }
      
    // Navigate based on selection
    if (selectedOption === 'burial') {
      navigate('../burial-arrangements');
    } else if (selectedOption === 'cremation') {
      navigate('../cremation-arrangements');
    } else if (selectedOption === 'donation') {
      navigate('../donation-arrangements');
    } else if (selectedOption === 'no-preference') {
      navigate('../ceremony-preferences');
    }
  }, [ navigate, funeralArrangement, handleCreateOrUpdate, update ]);

  const memoizedOptions = useMemo(() => options, []);

  return (
    <div className="new-fa-disposition screen">
      <ScreenHeader 
        title="Disposition Preferences"
        description="Select your preferred method of disposition."
        steps={6}
        step={1}
        handleBack={handleBack}
      />
      <RadioCardOptionGroup options={memoizedOptions} />
      <ErrorMessage message={error || errors[0]} />
      <ButtonBar
        handlePrimary={handleNext}
      />
    </div>
  );
}

